<template>
  <v-btn
    width="100%"
    height="96"
    v-ripple
    outlined
    :color="dragOver ? 'primary' : ''"
    class="rounded-10 fill-height py-1 grey-border"
    style="cursor: pointer; user-select: none; background-color: #f5f5f5"
    @drop.prevent="onDrop($event)"
    @dragover.prevent="dragOver = true"
    @dragenter.prevent="dragOver = true"
    @dragleave.prevent="dragOver = false"
    @click="addFile()"
    :disabled="disabled || loading"
  >
    <div class="d-flex flex-column">
      <v-progress-circular
        v-if="loading"
        color="primary"
        :value="uploadProgress"
      />
      <span v-if="!loading">
        <v-icon class="mr-1">$upload</v-icon>
        UPLOAD
      </span>
      <i v-if="!loading" style="font-size: 10px; text-transform: none">
        Click or Drag and Drop to upload
      </i>
      <i
        v-if="!loading && (invalidFile || maxFilesExceeded)"
        style="font-size: 10px; text-transform: none"
        class="error--text"
      >
        <span v-if="maxFilesExceeded"> Only one file can be uploaded. </span>
        <span v-if="invalidFile && fileType === 'scan'">
          File type must be .stl or .ply
        </span>
        <span v-if="invalidFile && fileType === 'photo'">
          File type must be one of: .jpg, .jpeg, .png
        </span>
      </i>
    </div>
  </v-btn>
</template>

<script>
import { mapGetters } from "vuex"
import client from "@/lib/ApiClient"

export default {
  name: "FileUploader",

  props: [
    "text",
    "value",
    "category",
    "disabled",
    "newOrder",
    "fileType",
    "multiple"
  ],

  watch: {
    value(value) {
      if (value) {
        this.invalidFile = false
        this.filesToUpload = []
      }
    }
  },

  data() {
    return {
      loading: false,
      dragOver: false,
      filesToUpload: [],
      filesUploaded: [],
      failedUpload: [],
      uploadProgress: 0,
      inputDOMElement: null,
      invalidFile: false,
      maxFilesExceeded: false
    }
  },

  computed: {
    ...mapGetters(["selectedOrder", "selectedOrderSession", "country"])
  },

  methods: {
    fileExtensionCheck(file) {
      let ext = file.name.toLowerCase().split(".").pop()
      if (this.fileType === "other") return true

      switch (ext) {
        case "jpg":
        case "jpeg":
        case "png":
          return this.fileType === "photo"
        case "ply":
        case "stl":
          return this.fileType === "scan"
        default:
          return false
      }
    },

    async addFile() {
      this.maxFilesExceeded = false
      this.invalidFile = false
      if (this.inputDOMElement) {
        this.inputDOMElement.value = ""
      } else {
        this.inputDOMElement = document.createElement("input")
        this.inputDOMElement.type = "file"
        this.inputDOMElement.multiple = this.multiple
        this.inputDOMElement.addEventListener("change", async (event) => {
          const maxFiles = this.multiple ? 15 : 1

          if (event.target.files.length > maxFiles) {
            this.maxFilesExceeded = true
            return
          }

          let files = [...event.target.files]

          files.forEach((file) => {
            if (!this.fileExtensionCheck(file)) {
              this.invalidFile = true
              return
            }
            this.filesToUpload.push(file)
          })

          if (this.invalidFile) return

          if (this.newOrder)
            this.multiple
              ? this.$emit("input", this.filesToUpload)
              : this.$emit("input", this.filesToUpload[0])
          else {
            this.loading = true
            await this.handleFileUpload(this.filesToUpload)
            this.loading = false
          }
        })
      }
      this.inputDOMElement.click()
    },

    async onDrop(event) {
      this.dragOver = false
      this.maxFilesExceeded = false
      this.invalidFile = false
      const maxFiles = this.multiple ? 15 : 1

      if (event.dataTransfer.files.length > maxFiles) {
        this.maxFilesExceeded = true
        return
      }

      let files = [...event.dataTransfer.files]
      files.forEach((file) => {
        if (!this.fileExtensionCheck(file)) {
          this.invalidFile = true
          return
        }
        this.filesToUpload.push(file)
      })
      if (this.invalidFile) return

      if (this.newOrder) {
        this.multiple
          ? this.$emit("input", this.filesToUpload)
          : this.$emit("input", this.filesToUpload[0])
      } else {
        this.loading = true
        await this.handleFileUpload(this.filesToUpload)
        this.loading = false
      }
    },

    async handleFileUpload(files) {
      if (!files) return
      this.uploadProgress = 0

      const uploadedFiles = await client.oft.uploadFiles(
        files,
        (progressValue) => {
          this.uploadProgress = progressValue
        }
      )

      let payload = []
      uploadedFiles.forEach((file) => {
        if (file.uploaded) {
          let payloadObj = {
            file_name: file.oftFileName,
            file_category: this.category,
            file_type: this.fileType,
            oft_file_uid: file.oftUid
          }

          payload.push(payloadObj)
        } else this.failedUploads.push(file)
      })

      const response = await client.instance.orders.addFiles(
        this.selectedOrder.uid,
        this.selectedOrderSession.uid,
        payload
      )
      this.multiple
        ? this.$emit("input", response)
        : this.$emit("input", response[0])
    }
  }
}
</script>
